import axios from 'axios';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { PlayerListQueryConfig, PlayerWithStats, PaginatedResponse } from '@repo/types';
import API from '../endpoints';
import { getAccessToken } from '../utils/getAccessToken';
import { useGlobalContext } from '@slc/tools/GlobalContext';

export type PlayerListQueryParams = {
  query: PlayerListQueryConfig;
  page?: number;
  // Runtime overrides
  team?: string;
  competition?: string;
  season?: string;
};

export type PlayerListQueryResult = PaginatedResponse<PlayerWithStats>;

/**
 * React Query hook for fetching player stats using the query builder configuration.
 */
export const usePlayerListQuery = (params: PlayerListQueryParams, enabled = true) => {
  const { apiUrl } = useGlobalContext();

  return useQuery({
    queryKey: ['playerListStats', params, apiUrl],
    queryFn: () => fetchPlayerListStats(apiUrl, params),
    enabled: enabled && !!apiUrl,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

const fetchPlayerListStats = async (apiUrl: string, params: PlayerListQueryParams): Promise<PlayerListQueryResult> => {
  const accessToken = getAccessToken();

  const requestBody = {
    query: params.query,
    page: params.page ?? 1,
    team: params.team,
    competition: params.competition,
    season: params.season,
  };

  const { data } = await axios.post<PlayerListQueryResult>(
    `${apiUrl}${API.endpoints.footballPlayersStatsQuery}`,
    requestBody,
    {
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          }
        : {
            'Content-Type': 'application/json',
          },
    }
  );
  return data;
};

export type InfinitePlayerListQueryParams = Omit<PlayerListQueryParams, 'page'>;

/**
 * React Query infinite hook for fetching player stats with "load more" pagination.
 */
export const useInfinitePlayerListQuery = (params: InfinitePlayerListQueryParams, enabled = true) => {
  const { apiUrl } = useGlobalContext();

  return useInfiniteQuery({
    queryKey: ['playerListStatsInfinite', params, apiUrl],
    queryFn: async ({ pageParam = 1 }) => {
      const accessToken = getAccessToken();
      const requestBody = {
        query: params.query,
        page: pageParam,
        team: params.team,
        competition: params.competition,
        season: params.season,
      };

      const { data } = await axios.post<PlayerListQueryResult>(
        `${apiUrl}${API.endpoints.footballPlayersStatsQuery}`,
        requestBody,
        {
          headers: accessToken
            ? {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              }
            : {
                'Content-Type': 'application/json',
              },
        }
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    enabled: enabled && !!apiUrl,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export default usePlayerListQuery;
